import { DEFAULT_UNIT } from "@/config/app-defaults";
import { useState } from "react";

import { useClientConfig } from "../client-config/use-client-config";
import { SESSION_OVERRIDE_KEY } from "./constants";
import { UnitContext, type UnitContextValue } from "./UnitContext.shared";
import { readSessionOverride } from "./utils";

/**
 * Unit Provider
 *
 * Manages unit system state for the application.
 * Supports two levels of unit preference:
 * 1. Default preference (from ClientConfig, set in debug modal's client config form)
 * 2. Session override (temporary, set during session, stored in sessionStorage)
 */
export const UnitProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { clientConfig } = useClientConfig();
  const configUnit = clientConfig?.unit;

  const [sessionOverride, setSessionOverride] = useState<
    "metric" | "yards" | null
  >(readSessionOverride);

  // Derived during render — no effect needed; updates automatically when
  // configUnit (async-loaded) or sessionOverride (user action) changes.
  const defaultUnit: "metric" | "yards" = configUnit ?? DEFAULT_UNIT;
  const unit: "metric" | "yards" = sessionOverride ?? defaultUnit;

  const setUnit = (newUnit: "metric" | "yards") => {
    setSessionOverride(newUnit);
    try {
      sessionStorage.setItem(SESSION_OVERRIDE_KEY, newUnit);
    } catch (error) {
      console.error("Failed to save session unit override:", error);
    }
  };

  const resetToDefault = () => {
    setSessionOverride(null);
    try {
      sessionStorage.removeItem(SESSION_OVERRIDE_KEY);
    } catch (error) {
      console.error("Failed to clear session unit override:", error);
    }
  };

  const value: UnitContextValue = {
    unit,
    defaultUnit,
    setUnit,
    resetToDefault,
  };

  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
};
